import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Compass } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionLabel, Disclaimer } from "@/components/Shared";
import { PHILOSOPHY } from "@/data/content";

const CAREERS = ["Financial Education", "Market Research", "Content", "Data Analysis", "Technology", "Course Production", "Video", "Design", "Community", "Student Relations"];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About KRV Prime"
        title="Building a Culture of Systematic Market Learning"
        subtitle="An independent financial-market education and information platform built around systematic thinking, disciplined analysis and continuous learning."
        crumbs={[{ label: "About" }]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        <section className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionLabel>Who We Are</SectionLabel>
            <p className="text-slate-600 leading-relaxed">
              KRV Prime is an independent financial-market education and information platform designed to provide
              systematic educational resources for individuals interested in understanding financial markets, technical
              analysis, derivatives, market data and systematic trading concepts.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We cover subjects including Central Pivot Range (CPR), Traditional and Camarilla Pivot Points, technical
              analysis, price action, market structure, Open Interest, Put-Call Ratio, options, futures, trading
              psychology, risk management and other market-related concepts. Resources may include free and paid courses,
              webinars, books, articles, historical market-chart examples, analytical tools and market-data insights.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Our objective is to provide systematic learning and analytical frameworks that help users study and
              understand financial markets independently — not to reduce markets to isolated signals or personalised advice.
            </p>
          </div>
          <div className="grid gap-4">
            {[
              { icon: Eye, title: "Our Vision", body: "To be a respected financial-market education platform that helps individuals develop systematic, independent and responsible market thinking." },
              { icon: Target, title: "Our Mission", body: "To make complex financial-market concepts easier to understand through systematic education, practical frameworks and technology-enabled learning." },
            ].map((c) => (
              <div key={c.title} className="border border-border rounded-lg p-7 bg-white">
                <span className="inline-flex h-11 w-11 rounded-md bg-navy items-center justify-center"><c.icon className="h-5 w-5 text-gold" /></span>
                <h3 className="font-display text-xl font-semibold text-navy mt-4">{c.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <SectionLabel>Our Philosophy</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-10">Five Principles</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border rounded-lg overflow-hidden">
            {PHILOSOPHY.map((p, i) => (
              <div key={p.title} className="bg-white p-6" data-testid={`philosophy-${p.title.toLowerCase()}`}>
                <span className="font-display text-3xl font-bold text-slate-200">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-lg font-semibold text-navy mt-2">{p.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-navy grain rounded-2xl p-10 lg:p-14">
          <SectionLabel light>Careers</SectionLabel>
          <h2 className="font-display text-3xl font-bold text-white mb-3">Build With KRV Prime</h2>
          <p className="text-slate-400 max-w-2xl mb-8">We are always interested in people who care about systematic financial-market education.</p>
          <div className="flex flex-wrap gap-2 mb-8">
            {CAREERS.map((c) => <span key={c} className="text-sm px-3 py-1.5 rounded-md border border-white/15 text-slate-300">{c}</span>)}
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors" data-testid="careers-cta">
            Submit Your Profile <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <Disclaimer />
      </div>
    </div>
  );
}
